"use client";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LibraryMusicIcon from "@mui/icons-material/LibraryMusic";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import QueueMusicIcon from "@mui/icons-material/QueueMusic";
import {
  animate,
  AnimatePresence,
  motion,
  Transition,
  useMotionValue,
  Variants,
} from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CastButton from "~/components/audio/CastButton";
import PlayerControls, {
  PLAYER_CONTROLS_HEIGHT,
} from "~/components/audio/PlayerControls";
import SwipeableViews from "~/components/SwipeableViews";
import type { Song } from "~/types";
import CustomerLyricsDisplay from "../CustomerLyricsDisplay";
import type {
  SongActionButton,
  SongActionContextMenuItem,
  SongActionCustomButton,
  SongActionPanelButton,
  SongActionPanelType,
  SongActionWithDialog,
} from "./songActions.types";
import SongArtwork from "./SongArtwork";
import {
  useAudioControls,
  useAudioMiniPlayerState,
  useAudioPrevNextState,
  useCurrentSongActions,
} from "./useAudio";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";

interface AudioPlayerFullProps {
  song: Song;
  playlist: Song[];
}

// Drag-to-dismiss thresholds
const DISMISS_OFFSET_PX = 150; // minimum downward drag distance to dismiss the panel
const DISMISS_VELOCITY_PX_S = 500; // minimum swipe velocity (px/s) to dismiss
const DRAG_START_PX = 8; // minimum downward movement before starting panel drag
const BUTTONS_HEIGHT = 48; // height of the buttons row, used to calculate the panel height during drag

const PANEL_BUTTON_ICONS: Record<
  SongActionPanelType,
  { IconComponent: React.ComponentType; label: string }
> = {
  lyrics: { IconComponent: LibraryMusicIcon, label: "Versuri" },
  queue: { IconComponent: QueueMusicIcon, label: "Coada" },
};

export default function AudioPlayerFull({
  song,
  playlist,
}: AudioPlayerFullProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const currentPlaylistIndex = playlist.findIndex((s) => s.id === song.id);
  const { playSong, prev, next, close, open, pause, resume } =
    useAudioControls();
  const { loop } = useAudioPrevNextState();
  const { isPlaying, isCasting, castingDeviceName } = useAudioMiniPlayerState();
  const songActions = useCurrentSongActions();

  const [panelOpened, setPanelOpened] = useState<SongActionPanelType | null>(
    null,
  );
  // panelDragY drives the panel's Y offset imperatively (drag + spring-back + slide-out)
  const panelDragY = useMotionValue(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<HTMLDivElement>(null);

  // Context menu anchor and active dialog
  const [ctxMenuAnchor, setCtxMenuAnchor] = useState<HTMLElement | null>(null);
  const [activeDialogAction, setActiveDialogAction] =
    useState<SongActionWithDialog | null>(null);

  const handleCtxMenuOpen = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setCtxMenuAnchor(e.currentTarget);
  }, []);
  const handleCtxMenuClose = useCallback(() => {
    setCtxMenuAnchor(null);
  }, []);
  const handleCtxMenuItemClick = useCallback(
    (action: SongActionContextMenuItem) => {
      setCtxMenuAnchor(null);
      if ("DialogComponent" in action && action.DialogComponent) {
        setActiveDialogAction(action);
      } else if ("onContextMenuClick" in action && action.onContextMenuClick) {
        action.onContextMenuClick();
      }
    },
    [],
  );
  const handleBtnClick = useCallback((action: SongActionButton) => {
    if ("DialogComponent" in action && action.DialogComponent) {
      setActiveDialogAction(action);
    } else if (
      "buttonProps" in action &&
      "onClick" in action.buttonProps &&
      action.buttonProps.onClick
    ) {
      action.buttonProps.onClick();
    }
  }, []);

  const { buttonActions, ctxMenuActions } = useMemo(() => {
    const btns = songActions
      .filter((a) => a.visible)
      .filter(
        (a): a is SongActionButton => "buttonProps" in a && !!a.buttonProps,
      );
    const customBtns = songActions
      .filter((a) => a.visible)
      .filter(
        (a): a is SongActionCustomButton =>
          "CustomButtonComponent" in a && !!a.CustomButtonComponent,
      );
    const panelBtns = songActions
      .filter((a) => a.visible)
      .filter((a): a is SongActionPanelButton => "panel" in a && !!a.panel);

    // Build actions to show in bottom bar (preserve order from songActions)
    const actionsForBar = (
      [] as (
        | SongActionCustomButton
        | SongActionButton
        | SongActionPanelButton
      )[]
    )
      .concat(customBtns)
      .concat(btns)
      .concat(panelBtns)
      .sort((a, b) => {
        const aIndex = songActions.findIndex((sa) => sa === a);
        const bIndex = songActions.findIndex((sa) => sa === b);
        return aIndex - bIndex;
      });

    const ctx = songActions.filter(
      (a): a is SongActionContextMenuItem =>
        "inContextMenu" in a && a.inContextMenu,
    );

    return {
      buttonActions: actionsForBar,
      ctxMenuActions: ctx,
    };
  }, [songActions]);

  // Close the panel via a downward animation then unmount
  const closePanelAnimated = useCallback(() => {
    const h = typeof window !== "undefined" ? window.innerHeight : 800;
    animate(panelDragY, h, {
      type: "tween",
      duration: 0.22,
      ease: "easeIn",
      onComplete: () => {
        setPanelOpened(null);
      },
    });
  }, [panelDragY]);

  // Attach scroll-aware touch handlers to the scroll container (mobile only)
  useEffect(() => {
    if (!isMobile || !panelOpened) return;
    const scrollEl = scrollContainerRef.current;
    if (!scrollEl) return;

    let startY = 0;
    let lastY = 0;
    let lastTime = 0;
    let velocityY = 0;
    let panelDragActive = false;

    const onTouchStart = (e: TouchEvent) => {
      startY = e.touches[0].clientY;
      lastY = startY;
      lastTime = Date.now();
      panelDragActive = false;
      velocityY = 0;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!e.touches.length) return;
      const currentY = e.touches[0].clientY;
      const deltaY = currentY - startY;
      const now = Date.now();
      const dt = now - lastTime;
      if (dt > 0) velocityY = ((currentY - lastY) / dt) * 1000;
      lastY = currentY;
      lastTime = now;

      if (!panelDragActive) {
        // Only start panel drag when at the top of the scroll and dragging down
        if (deltaY > DRAG_START_PX && scrollEl.scrollTop <= 0) {
          panelDragActive = true;
        } else {
          // Let the browser scroll normally
          return;
        }
      }

      if (panelDragActive) {
        // Only downward movement moves the panel; upward is clamped to 0
        panelDragY.set(Math.max(0, deltaY));
        e.preventDefault();
      }
    };

    const onTouchEnd = () => {
      if (!panelDragActive) return;
      panelDragActive = false;
      const offset = panelDragY.get();
      const shouldClose =
        offset > DISMISS_OFFSET_PX || velocityY > DISMISS_VELOCITY_PX_S;
      if (shouldClose) {
        closePanelAnimated();
      } else {
        animate(panelDragY, 0, { type: "spring", stiffness: 400, damping: 30 });
      }
    };

    scrollEl.addEventListener("touchstart", onTouchStart, { passive: true });
    scrollEl.addEventListener("touchmove", onTouchMove, { passive: false });
    scrollEl.addEventListener("touchend", onTouchEnd, { passive: true });
    scrollEl.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      scrollEl.removeEventListener("touchstart", onTouchStart);
      scrollEl.removeEventListener("touchmove", onTouchMove);
      scrollEl.removeEventListener("touchend", onTouchEnd);
      scrollEl.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [isMobile, panelOpened, closePanelAnimated, panelDragY]);

  // Attach touch handlers to the drag handle (always allows panel drag downward)
  useEffect(() => {
    if (!isMobile || !panelOpened) return;
    const handleEl = dragHandleRef.current;
    if (!handleEl) return;

    let startY = 0;
    let lastY = 0;
    let lastTime = 0;
    let velocityY = 0;

    const onTouchStart = (e: TouchEvent) => {
      startY = e.touches[0].clientY;
      lastY = startY;
      lastTime = Date.now();
      velocityY = 0;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!e.touches.length) return;
      const currentY = e.touches[0].clientY;
      const deltaY = currentY - startY;
      const now = Date.now();
      const dt = now - lastTime;
      if (dt > 0) velocityY = ((currentY - lastY) / dt) * 1000;
      lastY = currentY;
      lastTime = now;
      // Drag handle: clamp to downward-only movement
      panelDragY.set(Math.max(0, deltaY));
      e.preventDefault();
    };

    const onTouchEnd = () => {
      const offset = panelDragY.get();
      const shouldClose =
        offset > DISMISS_OFFSET_PX || velocityY > DISMISS_VELOCITY_PX_S;
      if (shouldClose) {
        closePanelAnimated();
      } else {
        animate(panelDragY, 0, { type: "spring", stiffness: 400, damping: 30 });
      }
    };

    handleEl.addEventListener("touchstart", onTouchStart, { passive: true });
    handleEl.addEventListener("touchmove", onTouchMove, { passive: false });
    handleEl.addEventListener("touchend", onTouchEnd, { passive: true });
    handleEl.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      handleEl.removeEventListener("touchstart", onTouchStart);
      handleEl.removeEventListener("touchmove", onTouchMove);
      handleEl.removeEventListener("touchend", onTouchEnd);
      handleEl.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [isMobile, panelOpened, closePanelAnimated, panelDragY]);

  const handlePrev = () => {
    prev();
    open();
  };
  const handleNext = () => {
    next();
    open();
  };

  const handleIndexChange = (target: Song) => {
    playSong(target, playlist);
    open();
  };

  // Close the full player when the Escape key is pressed
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [close]);

  const transition: Transition = { type: "tween", duration: 0.28 };
  const panelVariants: Variants = isMobile
    ? { hidden: { y: "100%", transition }, visible: { y: 0, transition } }
    : { hidden: { x: "100%", transition }, visible: { x: 0, transition } };
  const mainVariants: Variants = {
    closed: { right: "0px", transition },
    open: { right: "420px", transition },
  };

  const BOTTOM_HEIGHT = PLAYER_CONTROLS_HEIGHT + BUTTONS_HEIGHT + 16; // controls + buttons + gap

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: "rgba(0,0,0,0.85)",
        zIndex: 9999,
      }}
    >
      <Box
        component={motion.div}
        initial="closed"
        animate={panelOpened && !isMobile ? "open" : "closed"}
        variants={mainVariants}
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0
          }}>
          {/* Compact mobile header (shared layout animation) */}
          <AnimatePresence>
            {isMobile && panelOpened && (
              <Box
                key="mobile-compact-header"
                component={motion.div}
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 4 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.22 }}
                onClick={() => setPanelOpened(null)}
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  px: 2,
                  py: 1,
                  zIndex: 13,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "common.white",
                  cursor: "pointer",
                }}
              >
                <Box
                  component={motion.div}
                  layoutId={`artwork-player-${song.id}`}
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 1,
                    overflow: "hidden",
                    flex: "0 0 auto",
                    boxShadow: 6,
                    bgcolor: "action.disabledBackground",
                  }}
                >
                  <SongArtwork song={song} size="100%" hidePlayingBars />
                </Box>

                <Box
                  sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      color: "common.white",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {song.title}
                  </Typography>
                  {song.description ? (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "common.white",
                        opacity: 0.9,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {song.description}
                    </Typography>
                  ) : null}
                </Box>
                {/* Play/Pause for compact header (stop event propagation to avoid closing panel) */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    mt: 1,
                    mr: 2,
                    zIndex: 14,
                  }}
                >
                  <IconButton
                    size="large"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isPlaying) pause();
                      else resume();
                    }}
                    sx={{ color: "common.white" }}
                  >
                    {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                  </IconButton>
                </Box>
              </Box>
            )}
          </AnimatePresence>

          {/* Blurred artwork background with slow pan animation */}
          {song.artworkSrc && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 0,
                overflow: "hidden",
                filter: "blur(20px) brightness(0.4)",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: "-10%",
                  "@keyframes artworkPan": {
                    "0%": { transform: "scale(1.2) translate(0%, 0%)" },
                    "25%": { transform: "scale(1.25) translate(3%, 2%)" },
                    "50%": { transform: "scale(1.2) translate(-2%, 4%)" },
                    "75%": { transform: "scale(1.25) translate(-4%, -2%)" },
                    "100%": { transform: "scale(1.2) translate(0%, 0%)" },
                  },
                  animation: "artworkPan 20s ease-in-out infinite",
                }}
              >
                <SongArtwork song={song} size="100%" disableVideo />
              </Box>
            </Box>
          )}

          {/* dark gradient overlay on bottom half to improve contrast for title/buttons */}
          <Box
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: "55%",
              zIndex: 1,
              pointerEvents: "none",
              backgroundImage:
                "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.9) 100%)",
            }}
          />
          {/* subtle top fade so icons on the top corners are readable */}
          <Box
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              top: 0,
              height: "18%",
              zIndex: 1,
              pointerEvents: "none",
              backgroundImage:
                "linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)",
            }}
          />

          {/* Back / minimize button (fades when compact header is shown on mobile) */}
          <Box
            component={motion.div}
            animate={{ opacity: isMobile && panelOpened ? 0 : 1 }}
            transition={{ duration: 0.22 }}
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              zIndex: 12,
              pointerEvents: isMobile && panelOpened ? "none" : "auto",
            }}
          >
            <IconButton
              size="small"
              onClick={() => close()}
              sx={{ color: "common.white" }}
            >
              <ExpandMoreIcon />
            </IconButton>
          </Box>

          {/* middle section, show casting status */}
          <Box
            component={motion.div}
            animate={{ opacity: isCasting ? 1 : 0 }}
            transition={{ duration: 0.22 }}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 11,
              pointerEvents: "none",
              transform: "translateY(50%)",
            }}
          >
            {/* Casting indicator */}
            {isCasting && (
              <Typography
                variant="caption"
                sx={{
                  color: "primary.main",
                  textAlign: "center",
                  display: "block",
                  mr: 1,
                }}
              >
                {castingDeviceName
                  ? `Redare pe ${castingDeviceName}`
                  : "Redare pe TV"}
              </Typography>
            )}
          </Box>

          {/* Cast button and context menu top-right (fades when compact header is shown on mobile) */}
          <Box
            component={motion.div}
            animate={{ opacity: isMobile && panelOpened ? 0 : 1 }}
            transition={{ duration: 0.22 }}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 10,
              color: "common.white",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              pointerEvents: isMobile && panelOpened ? "none" : "auto",
            }}
          >
            <CastButton />
            {ctxMenuActions.length > 0 && (
              <IconButton
                size="small"
                onClick={handleCtxMenuOpen}
                sx={{ color: "common.white" }}
                aria-label="Mai multe opțiuni"
              >
                <MoreVertIcon />
              </IconButton>
            )}
          </Box>

          {/* Context menu */}
          <Menu
            anchorEl={ctxMenuAnchor}
            open={Boolean(ctxMenuAnchor)}
            onClose={handleCtxMenuClose}
          >
            {ctxMenuActions.map((action, idx) => (
              <MenuItem
                key={idx}
                onClick={() => handleCtxMenuItemClick(action)}
              >
                {action.contextMenuLabel}
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {/* Artwork + title */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            height: `calc(100vh - ${BOTTOM_HEIGHT}px)`,
          }}
        >
          <SwipeableViews
            options={{
              loop: loop,
              startSnap: currentPlaylistIndex,
              ssr: playlist.map((s) => 100),
            }}
            onIndexChange={(i) => {
              const target = playlist[i];
              if (target && target.id !== song.id) {
                handleIndexChange(target);
              }
            }}
          >
            {playlist.map((s) => (
              <Box
                key={s.id}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Box
                  component={motion.div}
                  layoutId={`artwork-player-${s.id}`}
                  sx={{
                    width: { xs: "80%", md: 420 },
                    height: { xs: "auto", md: 420 },
                    maxHeight: "calc(100vh - 400px)",
                    maxWidth: "calc(100vh - 400px)",
                    aspectRatio: "1 / 1",
                    borderRadius: 2,
                    boxShadow: 6,
                    position: "relative",
                    bgcolor: "action.disabledBackground",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <SongArtwork
                    song={s}
                    size="100%"
                    hidePlayingBars={s.id !== song.id}
                  />
                </Box>

                <Box sx={{ mt: 2, height: 60 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: "common.white",
                      textAlign: "center",
                      px: 2,
                    }}
                  >
                    {s.title}
                  </Typography>
                  {s.description && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "common.white",
                        mt: 0.5,
                        WebkitLineClamp: 1,
                        display: "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textAlign: "center",
                        px: 4,
                      }}
                    >
                      {s.description}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </SwipeableViews>
        </Box>

        {/* Bottom controls */}
        <Box
          sx={{
            width: "100%",
            position: "absolute",
            bottom: 32,
            left: 0,
            right: 0,
            zIndex: 5,
            display: "flex",
            justifyContent: "center",
            height: BOTTOM_HEIGHT,
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: "md",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: `${BOTTOM_HEIGHT - PLAYER_CONTROLS_HEIGHT - BUTTONS_HEIGHT}px`,
            }}
          >
            {/* buttons bar */}
            <Box
              className="hide-scrollbar"
              sx={{
                height: BUTTONS_HEIGHT,
                display: "flex",
                alignItems: "center",
                gap: 1,
                justifyContent: "flex-start",
                overflowX: { xs: "auto", md: "visible" },
                overflowY: "hidden",
                WebkitOverflowScrolling: "touch",
                flexWrap: "nowrap",
                width: "100%",
                maxWidth: "100%",
                boxSizing: "border-box",
                "& > *": { flex: "0 0 auto" },
                "& > *:first-child": { ml: 2 },
                "& > *:last-child": { mr: 2 },
              }}
            >
              {buttonActions.map((action, idx) => {
                // If the action provides a customButtonComponent, render it directly
                if ("CustomButtonComponent" in action) {
                  const C = action.CustomButtonComponent;
                  return <C key={idx} song={song} />;
                }

                // If the action is a panel button, render a button that opens the corresponding panel
                if ("panel" in action) {
                  const panelIcon = PANEL_BUTTON_ICONS[action.panel];
                  const IconComponent = panelIcon
                    ? panelIcon.IconComponent
                    : null;
                  const label = panelIcon ? panelIcon.label : null;
                  return (
                    <Button
                      key={idx}
                      variant="audio"
                      startIcon={IconComponent ? <IconComponent /> : null}
                      onClick={() =>
                        setPanelOpened((o) =>
                          o === action.panel ? null : action.panel,
                        )
                      }
                    >
                      {label}
                    </Button>
                  );
                }

                // Otherwise render the standard button
                return (
                  <Button
                    key={idx}
                    variant="audio"
                    startIcon={action.buttonProps.startIcon}
                    onClick={() => handleBtnClick(action)}
                    sx={{
                      ...action.buttonProps.sx,
                    }}
                  >
                    {action.buttonProps.children}
                  </Button>
                );
              })}
            </Box>
            <Box sx={{ px: 2, width: "100%" }}>
              <PlayerControls onPrev={handlePrev} onNext={handleNext} />
            </Box>
          </Box>
        </Box>
      </Box>
      <AnimatePresence>
        {panelOpened && (
          <>
            <Box
              key="audio-panel"
              component={motion.div}
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={panelVariants}
              style={isMobile ? { y: panelDragY } : undefined}
              sx={{
                position: "fixed",
                zIndex: 9999,
                // Mobile: full-width panel from bottom. Desktop: narrow right-side panel.
                left: isMobile ? 0 : "auto",
                right: 0,
                bottom: 0,
                // on mobile, make the panel reach up to the compact header (header top:56px, height:56px => bottom at 112px)
                top: isMobile ? 80 : 0,
                width: { xs: "100%", md: 420 },
                // use top/bottom positioning on mobile instead of fixed height
                height: isMobile ? undefined : "100%",
                bgcolor: "rgb(0,0,0)",
                color: "common.white",
                display: "flex",
                flexDirection: "column",
                borderTopLeftRadius: 2,
                borderBottomLeftRadius: isMobile ? 0 : 2,
                borderTopRightRadius: isMobile ? 2 : 0,
                borderBottomRightRadius: 0,
              }}
            >
              {/* Mobile drag handle — touch here to drag the panel down and dismiss */}
              {isMobile && (
                <Box
                  ref={dragHandleRef}
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    py: 1.5,
                    flexShrink: 0,
                    cursor: "grab",
                    touchAction: "none",
                    userSelect: "none",
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 6,
                      borderRadius: 3,
                      bgcolor: "rgba(255,255,255,0.25)",
                    }}
                  />
                </Box>
              )}

              {/* Scrollable content area */}
              <Box
                ref={scrollContainerRef}
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  p: { xs: 2, md: 4 },
                  // custom scrollbar styling for WebKit and Firefox
                  "&::-webkit-scrollbar": { width: 8, height: 8 },
                  "&::-webkit-scrollbar-track": { background: "transparent" },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "rgba(255,255,255,0.12)",
                    borderRadius: 999,
                  },
                  "&:hover::-webkit-scrollbar-thumb": {
                    backgroundColor: "rgba(255,255,255,0.18)",
                  },
                  scrollbarWidth: "thin", // firefox
                  scrollbarColor: "rgba(255,255,255,0.12) transparent",
                }}
              >
                {song.lyrics ? (
                  <CustomerLyricsDisplay lyrics={song.lyrics} />
                ) : null}
              </Box>

              {/* Desktop: close X in top-right of the side panel */}
              {!isMobile && (
                <Box
                  sx={{ position: "absolute", top: 8, right: 8, zIndex: 12 }}
                >
                  <IconButton
                    size="small"
                    onClick={() => setPanelOpened(null)}
                    sx={{ color: "common.white" }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              )}
            </Box>
          </>
        )}
      </AnimatePresence>
      {/* Active action dialog */}
      {activeDialogAction?.DialogComponent && (
        <activeDialogAction.DialogComponent
          song={song}
          onClose={() => setActiveDialogAction(null)}
        />
      )}
    </Box>
  );
}
